import { useCallback, useMemo, useRef, useState } from 'react';
import { useFieldDefaults } from './defaults';
import { withValueAt } from './resolve';
import type { FieldDef, FieldValues } from './types';
import { validateFields, type FieldErrors } from './validate';

export interface FieldForm {
  values: FieldValues;
  errors: FieldErrors;
  isDirty: boolean;
  setValue: (name: string, value: unknown) => void;
  /** Show errors from elsewhere, e.g. the server's field errors after a save. */
  setErrors: (errors: FieldErrors) => void;
  /** Check every field; true when the form can be submitted. */
  validate: () => boolean;
  reset: (values: FieldValues) => void;
}

/** State and validation for a form described by field definitions. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useFieldForm(defs: FieldDef<any>[], initial: FieldValues): FieldForm {
  const defaults = useFieldDefaults();
  const baseline = useRef(initial);
  const [values, setValues] = useState(initial);
  const [errors, setErrors] = useState<FieldErrors>({});

  const setValue = useCallback((name: string, value: unknown) => {
    setValues((current) => withValueAt(current, name, value));
    setErrors((current) => {
      if (!(name in current)) return current;
      const { [name]: _cleared, ...rest } = current;
      return rest;
    });
  }, []);

  const validate = useCallback(() => {
    const found = validateFields(defs, values, defaults);
    setErrors(found);
    return Object.keys(found).length === 0;
  }, [defs, values, defaults]);

  const reset = useCallback((next: FieldValues) => {
    baseline.current = next;
    setValues(next);
    setErrors({});
  }, []);

  const isDirty = useMemo(() => JSON.stringify(values) !== JSON.stringify(baseline.current), [values]);
  return { values, errors, isDirty, setValue, setErrors, validate, reset };
}
